import { useCallback, useEffect, useState } from 'react';
import { api } from '../api.ts';
import { parseUtc } from '../format.ts';
import { SHOT_STATES, standardStateKey } from '../shotState.ts';
import type { SheetInfo, SheetProbe, SheetStatusLabels } from '../types.ts';
import { Chevron } from './Chevron.tsx';

interface Props {
  projectId: string;
}

const LABEL_FIELDS: { key: keyof SheetStatusLabels; caption: string }[] = [
  { key: 'approved', caption: 'approved' },
  { key: 'rejected', caption: 'rejected' },
  { key: 'in_review', caption: 'in a review' },
  { key: 'pending', caption: 'untouched' },
];

/** Roughly "2 min ago" — nothing here is worth a precise timestamp. */
function agoText(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - parseUtc(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  return `${Math.round(seconds / 86400)} days ago`;
}

/**
 * Links a project to the Google Sheet its shot list lives in. Approve/reject and every note are
 * mirrored into it from then on, and an approve or reject typed straight into the sheet is read
 * back the other way — except an approval already made here, which the sheet can never undo.
 * Nothing here ever touches the service-account key, which lives on the server and is only
 * named so the sheet can be shared with it.
 *
 * Once set up there is nothing to look at, so the section folds away: what is left is the sync
 * button, the link and anything that went wrong.
 */
export function SheetPanel({ projectId }: Props): React.JSX.Element {
  const [info, setInfo] = useState<SheetInfo | null>(null);
  const [probe, setProbe] = useState<SheetProbe | null>(null);
  const [url, setUrl] = useState('');
  const [sheetTitle, setSheetTitle] = useState('');
  const [headerRow, setHeaderRow] = useState(1);
  const [columns, setColumns] = useState<{ shot: string; status: string; notes: string; version: string }>({
    shot: '',
    status: '',
    notes: '',
    version: '',
  });
  const [labels, setLabels] = useState<SheetStatusLabels | null>(null);
  // Extra stage words beyond the four above (e.g. "In Progress"), and the colour each has —
  // auto-assigned by the server the first time a shot shows the word, repaintable from here.
  const [stageColors, setStageColors] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState<null | 'test' | 'save' | 'sync'>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Folded away by default; the choice is remembered per browser, like the review panels.
  const [open, setOpen] = useState(() => localStorage.getItem('openreview.sheetOpen') === '1');

  const toggle = useCallback(
    () =>
      setOpen((v) => {
        localStorage.setItem('openreview.sheetOpen', v ? '0' : '1');
        return !v;
      }),
    [],
  );

  const load = useCallback(async () => {
    try {
      const res = await api.getSheet(projectId);
      setInfo(res);
      setLabels((current) => current ?? res.link?.statusLabels ?? res.defaultLabels);
      setStageColors((current) => current ?? res.link?.stageColors ?? {});
      if (res.link) {
        setUrl((current) => current || res.link!.spreadsheetUrl);
        setSheetTitle((current) => current || res.link!.sheetTitle);
        setHeaderRow(res.link.headerRow);
        setColumns((current) =>
          current.shot
            ? current
            : {
                shot: res.link!.shotColumn,
                status: res.link!.statusColumn,
                notes: res.link!.notesColumn ?? '',
                version: res.link!.versionColumn ?? '',
              },
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runTest = async (): Promise<void> => {
    setBusy('test');
    setError(null);
    setNote(null);
    try {
      const res = await api.testSheet(projectId, { url, sheetTitle: sheetTitle || undefined, headerRow });
      setProbe(res);
      setSheetTitle(res.sheetTitle);
      setHeaderRow(res.headerRow);
      // Only fill in what is still blank: a mapping someone corrected by hand stays.
      setColumns((current) => ({
        shot: current.shot || res.guess.shot || '',
        status: current.status || res.guess.status || '',
        notes: current.notes || res.guess.notes || '',
        version: current.version || res.guess.version || '',
      }));
      setNote(`Connected — sheet “${res.sheetTitle}”, ${res.headers.length} columns read.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const save = async (): Promise<void> => {
    if (!labels) return;
    setBusy('save');
    setError(null);
    setNote(null);
    try {
      const res = await api.saveSheet(projectId, {
        url,
        sheetTitle,
        shotColumn: columns.shot,
        statusColumn: columns.status,
        notesColumn: columns.notes || null,
        versionColumn: columns.version || null,
        headerRow,
        statusLabels: labels,
        stageColors: stageColors ?? undefined,
      });
      setNote(`Saved — pushing ${res.queued} shot${res.queued === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const sync = async (): Promise<void> => {
    setBusy('sync');
    setError(null);
    setNote(null);
    try {
      const res = await api.syncSheet(projectId);
      setNote(`Synced — pushed ${res.queued} shot${res.queued === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (): Promise<void> => {
    if (!confirm('Unlink this sheet? The spreadsheet itself is left untouched.')) return;
    try {
      await api.unlinkSheet(projectId);
      setProbe(null);
      setUrl('');
      setSheetTitle('');
      setColumns({ shot: '', status: '', notes: '', version: '' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!info) return <section className="admin__section" />;

  const toggleButton = (
    <button
      type="button"
      className="sheet__toggle"
      onClick={toggle}
      title={open ? 'Collapse' : 'Expand'}
      aria-label={open ? 'Collapse Google Sheet settings' : 'Expand Google Sheet settings'}
    >
      <Chevron dir={open ? 'up' : 'down'} />
    </button>
  );

  // No key on the server: the form would be a dead end, so explain the one-time setup instead.
  if (!info.serviceAccount) {
    return (
      <section className="admin__section">
        <h2>
          {toggleButton}
          Google Sheet
        </h2>
        {open ? (
          <p className="muted">
            No Google service account on this server. One-time setup: create a project in the
            Google Cloud Console, enable the <strong>Google Sheets API</strong>, create a service
            account with a JSON key, and put that key in the server’s data directory as{' '}
            <code>google-service-account.json</code>. The address to share your sheet with then
            appears here.
          </p>
        ) : null}
      </section>
    );
  }

  const columnOptions = probe?.headers ?? [];
  const canSave = url.trim() !== '' && sheetTitle !== '' && columns.shot !== '' && columns.status !== '';

  const columnField = (
    field: 'shot' | 'status' | 'notes' | 'version',
    caption: string,
    optional = false,
  ): React.JSX.Element => (
    <label className="sheet__field">
      {caption}
      {columnOptions.length > 0 ? (
        <select value={columns[field]} onChange={(e) => setColumns({ ...columns, [field]: e.target.value })}>
          <option value="">{optional ? '— do not write —' : '— pick —'}</option>
          {columnOptions.map((header, index) => {
            const letter = String.fromCharCode(65 + index);
            return (
              <option key={letter} value={letter}>
                {letter} · {header || '(no heading)'}
              </option>
            );
          })}
        </select>
      ) : (
        <input
          value={columns[field]}
          placeholder={optional ? 'e.g. D (empty = off)' : 'e.g. A'}
          onChange={(e) => setColumns({ ...columns, [field]: e.target.value.toUpperCase() })}
        />
      )}
    </label>
  );

  return (
    <section className="admin__section">
      <h2>
        {toggleButton}
        Google Sheet
        {info.link ? (
          <span className="sheet__actions">
            <button type="button" onClick={() => void sync()} disabled={busy !== null}>
              {busy === 'sync' ? 'syncing…' : 'Sync now'}
            </button>
            <button type="button" className="danger" onClick={() => void unlink()}>
              Unlink
            </button>
          </span>
        ) : null}
      </h2>

      {open ? (
        <>
          <p className="muted">
            Approve and reject go into the status column, notes into the notes column. The sheet
            has to be shared with <code>{info.serviceAccount}</code> as an editor.
          </p>

          <div className="sheet__row">
            <label className="sheet__field sheet__field--wide">
              Sheet link
              <input
                value={url}
                placeholder="https://docs.google.com/spreadsheets/d/…"
                onChange={(e) => setUrl(e.target.value)}
              />
            </label>
            <button type="button" onClick={() => void runTest()} disabled={busy !== null || url.trim() === ''}>
              {busy === 'test' ? 'checking…' : 'Check connection'}
            </button>
          </div>

          <div className="sheet__row">
            <label className="sheet__field">
              Sheet
              {probe ? (
                <select value={sheetTitle} onChange={(e) => setSheetTitle(e.target.value)}>
                  {probe.tabs.map((tab) => (
                    <option key={tab} value={tab}>
                      {tab}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={sheetTitle} placeholder="tab name" onChange={(e) => setSheetTitle(e.target.value)} />
              )}
            </label>
            <label className="sheet__field sheet__field--narrow">
              Header row
              <input
                type="number"
                min={1}
                value={headerRow}
                onChange={(e) => setHeaderRow(Math.max(1, Number(e.target.value) || 1))}
              />
            </label>
            {columnField('shot', 'Shot column')}
            {columnField('status', 'Status column')}
            {columnField('notes', 'Notes column', true)}
            {columnField('version', 'Version column', true)}
          </div>

          {labels ? (
            <div className="sheet__row">
              {LABEL_FIELDS.map((field) => (
                <label key={field.key} className="sheet__field sheet__field--narrow">
                  {field.caption}
                  <input
                    value={labels[field.key]}
                    onChange={(e) => setLabels({ ...labels, [field.key]: e.target.value })}
                  />
                </label>
              ))}
            </div>
          ) : null}

          <div className="sheet__row">
            <div className="sheet__stage-colors">
              <p className="muted sheet__stage-hint">
                Status colours — each shot shows as a dot in its state's colour in the project view.
              </p>
              {Object.values(SHOT_STATES).map((state) => (
                <span key={state.label} className="sheet__stage-color">
                  <span className="sheet__legend-dot" style={{ background: state.bg }} aria-hidden="true" />
                  {state.label}
                </span>
              ))}
              {/* Words outside the standard set keep a colour of their own, repaintable here. */}
              {stageColors
                ? Object.entries(stageColors)
                    .filter(([text]) => standardStateKey(text) === null)
                    .map(([text, color]) => (
                      <label key={text} className="sheet__stage-color" title="Click the dot to repaint">
                        <input
                          type="color"
                          className="sheet__legend-dot"
                          value={color}
                          onChange={(e) => setStageColors({ ...stageColors, [text]: e.target.value })}
                        />
                        {text}
                      </label>
                    ))
                : null}
            </div>
          </div>

          <div className="sheet__row">
            <button type="button" className="primary" onClick={() => void save()} disabled={busy !== null || !canSave}>
              {busy === 'save' ? 'saving…' : 'Save'}
            </button>
            {info.link?.lastSyncAt ? <span className="muted">last pushed {agoText(info.link.lastSyncAt)}</span> : null}
            {info.queue.pending > 0 ? <span className="muted">{info.queue.pending} queued</span> : null}
          </div>

          {note ? <p className="sheet__note">{note}</p> : null}
        </>
      ) : null}

      {/* Failures stay visible while collapsed — a silent one would never be noticed. */}
      {error ? <p className="error">{error}</p> : null}
      {info.link?.lastError ? <p className="error">Google: {info.link.lastError}</p> : null}
      {info.queue.unmatched.length > 0 ? (
        <p className="error">
          No row in the sheet: {info.queue.unmatched.join(', ')} — match the shot names and sync again.
        </p>
      ) : null}
    </section>
  );
}
